<!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
<script setup>
import { computed } from 'vue';
import LaisPrimaryButton from 'dashboard/components-next/LaisPrimaryButton.vue';
import {
  contractActivityRows,
  contractDocumentLabel,
  contractProviderAlert,
  contractProviderName,
  contractProviderStatusLabel,
  contractSignerRows,
  contractStatusClass,
  contractStatusLabel,
} from '../utils/offerContractPresenter';

const props = defineProps({
  selectedOffer: { type: Object, default: null },
  selectedContract: { type: Object, default: null },
  selectedContractProviderStatus: { type: Object, default: null },
  pdfUrl: { type: String, default: '' },
  templateId: { type: String, default: '' },
  contractAction: { type: String, default: '' },
  canGenerateContract: { type: Boolean, default: false },
  canSyncContract: { type: Boolean, default: false },
  canCancelContract: { type: Boolean, default: false },
});

const emit = defineEmits([
  'update:pdfUrl',
  'update:templateId',
  'generate',
  'sync',
  'openCancel',
  'openSigner',
]);

const selectedContractSignerRows = computed(() =>
  contractSignerRows(props.selectedContract, props.selectedOffer)
);

const selectedContractActivityRows = computed(() =>
  contractActivityRows(
    props.selectedContract,
    props.selectedContractProviderStatus
  )
);

const selectedContractProviderAlert = computed(() =>
  contractProviderAlert(
    props.selectedContract,
    props.selectedContractProviderStatus
  )
);

const openContractSignerSignature = signer => emit('openSigner', signer);
</script>

<template>
  <!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
  <section
    class="offer-details-contract min-w-0 max-w-full rounded-lais border border-lais-border bg-white p-4"
  >
    <div class="mb-3 flex flex-wrap items-center justify-between gap-3">
      <h3 class="font-redHatDisplay text-[16px] font-semibold text-lais-text">
        CCV digital
      </h3>
      <span
        class="rounded-full px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wide"
        :class="contractStatusClass(selectedContract?.status)"
      >
        {{ contractStatusLabel(selectedContract?.status) }}
      </span>
    </div>

    <div
      v-if="selectedContractProviderAlert"
      role="alert"
      class="mb-3 rounded-lais-sm border border-rose-200 bg-rose-50 px-3 py-2 text-[12px] leading-relaxed text-rose-700"
    >
      <p class="font-semibold">{{ selectedContractProviderAlert.title }}</p>
      <p class="mt-1">{{ selectedContractProviderAlert.description }}</p>
    </div>

    <div v-if="selectedContract" class="grid gap-3 text-[13px]">
      <div class="grid gap-1 bg-lais-bg-soft px-3 py-2.5">
        <div class="offer-details-contract__detail-row grid gap-1.5">
          <span class="text-lais-text-muted">Documento</span>
          <span
            class="offer-details-contract__detail-value min-w-0 break-words font-semibold text-lais-text"
          >
            {{ contractDocumentLabel(selectedContract) }}
          </span>
        </div>
        <a
          v-if="selectedContract.signed_file_url"
          :href="selectedContract.signed_file_url"
          target="_blank"
          rel="noopener noreferrer"
          class="inline-flex items-center gap-1 font-semibold text-lais-link"
        >
          <span class="i-lucide-file-check-2 size-4" aria-hidden="true" />
          Arquivo assinado
        </a>
      </div>

      <dl
        v-if="selectedContractActivityRows.length"
        class="grid divide-y divide-lais-border-soft border-y border-lais-border-soft"
      >
        <div
          v-for="row in selectedContractActivityRows"
          :key="row.label"
          class="offer-details-contract__detail-row grid gap-1.5 py-2"
        >
          <dt class="text-lais-text-muted">{{ row.label }}</dt>
          <dd
            class="offer-details-contract__detail-value min-w-0 break-words font-semibold text-lais-text"
          >
            {{ row.value }}
          </dd>
        </div>
      </dl>

      <div
        v-if="selectedContractSignerRows.length"
        class="grid divide-y divide-lais-border-soft border-y border-lais-border-soft"
      >
        <div
          v-for="signer in selectedContractSignerRows"
          :key="signer.key"
          class="offer-details-contract__signer-row flex flex-col gap-3 py-3"
        >
          <div class="min-w-0">
            <div class="flex flex-wrap items-center gap-2">
              <p class="font-semibold text-lais-text">
                {{ signer.label }}
              </p>
              <span
                class="rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide"
                :class="signer.statusClass"
              >
                {{ signer.statusLabel }}
              </span>
            </div>
            <p class="mt-1 text-[12px] leading-5 text-lais-text-muted">
              {{ signer.description }}
            </p>
          </div>
          <a
            v-if="signer.signUrl"
            :href="signer.signUrl"
            target="_blank"
            rel="noopener noreferrer"
            class="inline-flex h-8 shrink-0 items-center justify-center gap-1 rounded-full border border-lais-border px-3 text-[12px] font-semibold text-lais-link transition-colors hover:border-lais-primary hover:bg-lais-primary-50"
            @click="openContractSignerSignature(signer)"
          >
            <span class="i-lucide-external-link size-3.5" aria-hidden="true" />
            {{ signer.actionLabel }}
          </a>
        </div>
      </div>
    </div>
    <dl
      v-else-if="selectedContractProviderStatus"
      class="grid divide-y divide-lais-border-soft bg-lais-bg-soft px-3 py-2 text-[13px]"
    >
      <div class="offer-details-contract__detail-row grid gap-1.5 py-2">
        <dt class="text-lais-text-muted">Provider</dt>
        <dd
          class="offer-details-contract__detail-value min-w-0 break-words font-semibold text-lais-text"
        >
          {{ contractProviderName(null, selectedContractProviderStatus) }}
        </dd>
      </div>
      <div class="offer-details-contract__detail-row grid gap-1.5 py-2">
        <dt class="text-lais-text-muted">Status</dt>
        <dd
          class="offer-details-contract__detail-value min-w-0 break-words font-semibold text-lais-text"
        >
          {{
            contractProviderStatusLabel(selectedContractProviderStatus.status)
          }}
        </dd>
      </div>
    </dl>

    <div class="offer-details-contract__fields mt-3 grid gap-3">
      <label class="grid gap-1 text-[12px] font-semibold text-lais-text-muted">
        PDF do CCV
        <input
          :value="pdfUrl"
          type="url"
          placeholder="https://..."
          class="h-10 rounded-lais border border-lais-border bg-white px-3 text-[14px] font-normal text-lais-text placeholder:text-lais-text-muted focus:border-lais-primary focus:outline-none"
          @input="emit('update:pdfUrl', $event.target.value)"
        />
      </label>
      <label class="grid gap-1 text-[12px] font-semibold text-lais-text-muted">
        Template ZapSign
        <input
          :value="templateId"
          type="text"
          placeholder="template_id"
          class="h-10 rounded-lais border border-lais-border bg-white px-3 text-[14px] font-normal text-lais-text placeholder:text-lais-text-muted focus:border-lais-primary focus:outline-none"
          @input="emit('update:templateId', $event.target.value)"
        />
      </label>
    </div>

    <div class="mt-3 flex flex-wrap gap-2">
      <LaisPrimaryButton
        v-if="canGenerateContract"
        size="sm"
        icon="i-lucide-send"
        :is-loading="contractAction === 'generate'"
        @click="emit('generate')"
      >
        Gerar CCV
      </LaisPrimaryButton>
      <LaisPrimaryButton
        v-if="canSyncContract"
        size="sm"
        variant="ghost"
        icon="i-lucide-refresh-cw"
        :is-loading="contractAction === 'sync'"
        @click="emit('sync')"
      >
        Sincronizar
      </LaisPrimaryButton>
      <LaisPrimaryButton
        v-if="canCancelContract"
        size="sm"
        variant="ghost"
        icon="i-lucide-ban"
        :is-loading="contractAction === 'cancel'"
        @click="emit('openCancel')"
      >
        Cancelar contrato
      </LaisPrimaryButton>
    </div>
  </section>
</template>

<style scoped>
.offer-details-contract {
  container-type: inline-size;
}

@container (min-width: 30rem) {
  .offer-details-contract__detail-row {
    grid-template-columns: minmax(0, 1fr) minmax(0, 1.25fr);
    align-items: start;
    gap: 0.75rem;
  }

  .offer-details-contract__detail-value {
    text-align: right;
  }
}

@container (min-width: 36rem) {
  .offer-details-contract__fields {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@container (min-width: 38rem) {
  .offer-details-contract__signer-row {
    align-items: center;
    flex-direction: row;
    justify-content: space-between;
  }
}
</style>
